import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import "./PrivacyPolicy.css";
import "../App.css";

export default function PrivacyPolicyPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <Link to="/" className="nav-logo-link">
              <img
                src="/belly-flow-logo.svg"
                alt="Belly Flow"
                className="nav-logo-img"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <Link to="/" className="nav-link">
              {t('features')}
            </Link>
            <Link to="/" className="nav-link">
              {t('about')}
            </Link>
            <Link to="/privacy" className="nav-link active">
              {t('privacy')}
            </Link>
            <Link to="/support" className="nav-link">
              {t('support')}
            </Link>
            
            {/* Language Switcher */}
            <div className="language-switcher">
              <button 
                className="language-button"
                onClick={toggleLanguage}
                aria-label={t('language')}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Right Side */}
          <div className="mobile-nav-right">
            <div className="mobile-language-switcher-top">
              <button 
                className="language-button mobile-top"
                onClick={toggleLanguage}
              >
                {language === 'sv' ? 'EN' : 'SV'}
              </button>
            </div>

            <button 
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Öppna meny"
            >
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-links">
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('features')}
            </Link>
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('about')}
            </Link>
            <Link to="/privacy" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('privacy')}
            </Link>
            <Link to="/support" className="mobile-nav-link" onClick={closeMobileMenu}>
              {t('support')}
            </Link>
          </div>
        </div>
      </nav>

      <div className="privacy-container">
        <h1 className="privacy-title">{t('privacyPolicyTitle')}</h1>

        <p className="privacy-date">
          {t('lastUpdated')} 30 juli 2026
        </p>

        <div className="privacy-content">
          <section>
            <h2 className="privacy-section-title">{t('introduction')}</h2>
            <p className="privacy-text">
              {t('introductionText')}
            </p>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('informationWeCollect')}</h2>

            <h3 className="privacy-subtitle">
              {t('dataLinkedToIdentity')}
            </h3>
            <div className="privacy-cards">
              <div className="privacy-card">
                <h4 className="privacy-card-title">{t('emailAddress')}</h4>
                <p className="privacy-card-highlight">
                  {t('usedFor')} {t('accountCreation')}
                </p>
                <p className="privacy-card-text">
                  {t('emailDescription')}
                </p>
              </div>

              <div className="privacy-card">
                <h4 className="privacy-card-title">{t('userContent')}</h4>
                <p className="privacy-card-highlight">
                  {t('usedFor')} {t('customerSupport')}
                </p>
                <p className="privacy-card-text">
                  {t('userContentDescription')}
                </p>
              </div>
            </div>

            <h3 className="privacy-subtitle">
              {t('dataNotLinkedToIdentity')}
            </h3>
            <div className="privacy-cards">
              <div className="privacy-card">
                <h4 className="privacy-card-title">{t('healthFitnessData')}</h4>
                <p className="privacy-card-highlight">
                  {t('usedFor')} {t('appFunctionality')}
                </p>
                <p className="privacy-card-text">
                  {t('healthDataDescription')}
                </p>
                <ul className="privacy-list">
                  <li>{t('gutSymptoms')}</li>
                  <li>{t('bristolStool')}</li>
                  <li>{t('dietInfo')}</li>
                  <li>{t('symptomDates')}</li>
                  <li>{t('personalNotes')}</li>
                </ul>
              </div>

              <div className="privacy-card">
                <h4 className="privacy-card-title">{t('contactInfo')}</h4>
                <p className="privacy-card-highlight">
                  {t('usedFor')} {t('technicalSupport')}
                </p>
                <p className="privacy-card-text">
                  {t('contactInfoDescription')}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="privacy-section-title">
              {t('howWeUseYourInformation')}
            </h2>

            <h3 className="privacy-subtitle">{t('primaryUseCases')}</h3>
            <div className="privacy-highlights">
              <div className="privacy-highlight">
                <h4 className="privacy-highlight-title">{t('appFunctionality')}</h4>
                <p>
                  {t('appFunctionalityDescription')}
                </p>
              </div>

              <div className="privacy-highlight">
                <h4 className="privacy-highlight-title">{t('customerSupport')}</h4>
                <p>
                  {t('customerSupportDescription')}
                </p>
              </div>

              <div className="privacy-highlight">
                <h4 className="privacy-highlight-title">{t('analysis')}</h4>
                <p>{t('analysisDescription')}</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('researchContributions')}</h2>
            <div className="privacy-warning">
              <p className="privacy-warning-text">
                <strong>{t('importantNotice')}:</strong> {t('researchContributionDescription')}
              </p>
              <ul className="privacy-list">
                <li>{t('identifyPatterns')}</li>
                <li>{t('developBetterRecommendations')}</li>
                <li>{t('promoteScientificKnowledge')}</li>
                <li>{t('supportBroaderMedicalResearch')}</li>
              </ul>
              <p className="privacy-warning-critical">
                <strong>{t('criticalNotice')}:</strong> {t('anonymizedDataOnly')}
              </p>
            </div>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('dataProtection')}</h2>

            <h3 className="privacy-subtitle">{t('securityMeasures')}</h3>
            <ul className="privacy-list">
              <li>
                {t('secureDatabase')}
              </li>
              <li>{t('allDataEncrypted')}</li>
              <li>
                {t('strictAccessToPersonalData')}
              </li>
              <li>{t('followsStandards')}</li>
            </ul>

            <h3 className="privacy-subtitle">{t('dataAnonymization')}</h3>
            <p className="privacy-text">
              {t('anonymizationDescription')}
            </p>
            <ul className="privacy-list">
              <li>{t('removeAllPersonalIdentifiers')}</li>
              <li>{t('dataAggregationTechniques')}</li>
              <li>{t('statisticalAnonymizationMethods')}</li>
              <li>{t('regularAuditOfAnonymizationProcesses')}</li>
            </ul>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('yourRightsAndChoices')}</h2>
            <p className="privacy-text">{t('yourRights')}:</p>
            <ul className="privacy-list">
              <li>{t('accessYourPersonalData')}</li>
              <li>{t('correctIncorrectInformation')}</li>
              <li>{t('deleteYourAccount')}</li>
              <li>{t('optOutOfResearchContributions')}</li>
              <li>{t('withdrawConsentAtAnyTime')}</li>
            </ul>

            <h3 className="privacy-subtitle">{t('dataStorage')}</h3>
            <ul className="privacy-list">
              <li>
                <strong>{t('accountData')}:</strong> {t('accountDataDescription')}
              </li>
              <li>
                <strong>{t('anonymizedResearchData')}:</strong> {t('anonymizedResearchDataDescription')}
              </li>
              <li>
                <strong>{t('personalIdentifiers')}:</strong> {t('personalIdentifiersDescription')}
              </li>
            </ul>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('thirdPartyServices')}</h2>
            <p className="privacy-text">
              {t('thirdPartyServicesDescription')}:
            </p>
            <ul className="privacy-list">
              <li>{t('dataStorageAndProcessing')}</li>
              <li>{t('analysisAndAppPerformanceMonitoring')}</li>
              <li>{t('customerSupportSystem')}</li>
              <li>{t('hostingServices')}</li>
            </ul>
            <p>
              {t('allThirdPartiesMustMaintain')}
            </p>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('childrensPrivacy')}</h2>
            <p className="privacy-text">
              {t('childrensPrivacyDescription')}
            </p>
          </section>

          <section>
            <h2 className="privacy-section-title">
              {t('internationalDataTransfers')}
            </h2>
            <p className="privacy-text">
              {t('internationalDataTransfersDescription')}
            </p>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('policyChanges')}</h2>
            <p className="privacy-text">
              {t('policyChangesDescription')}
            </p>
          </section>

          <section>
            <h2 className="privacy-section-title">{t('researchEthics')}</h2>
            <div className="privacy-info">
              <p className="privacy-info-text">
                {t('researchEthicsDescription')}:
              </p>
              <ul className="privacy-list">
                <li>{t('institutionalReviewBoardSupervision')}</li>
                <li>{t('complianceWithMedicalResearchEthicsStandards')}</li>
                <li>{t('commitmentToBeneficialResearchResults')}</li>
                <li>{t('transparencyInResearchMethodologies')}</li>
              </ul>
            </div>
          </section>

          <div className="privacy-contact">
            <h3 className="privacy-contact-title">{t('contactUs')}</h3>
            <p className="privacy-contact-text">
              {t('contactUsDescription')}:
            </p>
            <p>
              <strong>{t('email')}:</strong>{" "}
              <a
                href="mailto:support@bellyflow.se"
                className="privacy-contact-link"
              >
                support@bellyflow.se
              </a>
            </p>
          </div>

          <section>
            <h2 className="privacy-section-title">{t('consent')}</h2>
            <p className="privacy-text">
              {t('consentDescription')}
            </p>
          </section>

          <div className="privacy-compliance">
            <p className="privacy-compliance-text">
              <strong>{t('compliance')}:</strong> {t('complianceDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
